/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { CareerHighlight } from '@sportninja/common/features/profile/stats/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import Text from 'src/components/ts-components/Text'

import CareerStatsSkeleton from './skeletons/CareerStatsSkeleton'

// The season table's header caps.
const LABEL_STYLE = 'letter-spacing: 1.2px; text-transform: uppercase;'

interface CareerStatsProps {
  highlights: CareerHighlight[]
  /** Cold load of the season rows the career totals are summed from. */
  isLoading: boolean
}

/**
 * The selected sport's career totals side by side in a card — the key ones
 * (GP · G · A · P, or the goalie set) first, then a few more — each an
 * abbreviation over its value, the full name on hover. They share the card's
 * width and scroll sideways only when the column is too narrow. Nothing once
 * loaded without any (the season table carries a load error).
 */
const CareerStats = ({ highlights, isLoading }: CareerStatsProps) => {
  'use memo'

  if (!isLoading && highlights.length === 0) {
    return null
  }

  return (
    <section css={sectionStyles}>
      <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords('Career Stats')}
      </Text>
      {isLoading ? (
        <CareerStatsSkeleton />
      ) : (
        <div css={cardStyles}>
          {highlights.map(({ abbr, name, value }) => (
            <div
              key={abbr}
              css={itemStyles}
              role='group'
              title={name}
              aria-label={`${name}: ${value}`}
            >
              <Text
                variant='paragraphXSmall'
                weight='semiBold'
                color={colors.NEUTRAL_50}
                style={LABEL_STYLE}
              >
                {abbr}
              </Text>
              <Text variant='heading5' weight='bold' color={colors.WHITE}>
                {value}
              </Text>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const sectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// The This Week strip's card.
const cardStyles = css`
  display: flex;
  gap: 16px;
  padding: 16px 24px;
  overflow-x: auto;
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  background: ${colors.SECONDARY_400};
`

const itemStyles = css`
  flex: 1 0 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
`

export default CareerStats
